import React, { useState, useEffect } from "react";
import { userService } from "../../../_services";
import { FormDialog } from "../../ui/form-dialog";
import { Button } from "../../ui/button";
import { Alert, AlertDescription } from "../../ui/alert";
import { ChevronUp, ChevronDown, CheckCircle, AlertCircle } from "lucide-react";

/**
 * Sort Modules Modal
 * Modernized with shadcn Dialog - reorder training modules
 */
function SortModules({ hideModal, moduleData, renderVideos, title }) {
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [moduleList, setModuleList] = useState([]);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        setUserId(userId);
        setUserName(userName);

        if (moduleData) {
            setModuleList(moduleData);
        }
    }, [moduleData]);

    const swapUp = (currentIndex) => {
        const ItemCount = moduleList.length;
        let listData = [...moduleList];

        if (currentIndex === 0) {
            // Move first item to last
            const firstItem = listData[0];
            listData[0] = listData[ItemCount - 1];
            listData[ItemCount - 1] = firstItem;
        } else {
            // Swap with previous item
            const temp = listData[currentIndex];
            listData[currentIndex] = listData[currentIndex - 1];
            listData[currentIndex - 1] = temp;
        }

        setModuleList(listData);
    };

    const swapDown = (currentIndex) => {
        const ItemCount = moduleList.length;
        let listData = [...moduleList];

        if (currentIndex + 1 === ItemCount) {
            // Move last item to first
            const lastItem = listData[currentIndex];
            listData[currentIndex] = listData[0];
            listData[0] = lastItem;
        } else {
            // Swap with next item
            const temp = listData[currentIndex];
            listData[currentIndex] = listData[currentIndex + 1];
            listData[currentIndex + 1] = temp;
        }

        setModuleList(listData);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            const updatedData = moduleList.map(({ media, ...rest }) => rest);
            const SwappingJson = JSON.stringify(updatedData);

            const apidata = await userService.Swap_Module(userId, userName, encodeURIComponent(SwappingJson));

            if (apidata && apidata[0].code === '1') {
                setFormSuccess(apidata[0].message);
                setTimeout(() => {
                    hideModal();
                    renderVideos(userId, userName);
                }, 3000);
            } else {
                setFormError('Error saving order');
            }
        } catch (error) {
            setFormError('An error occurred while saving');
            console.error('Error sorting modules:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        hideModal();
    };

    return (
        <FormDialog
            open={true}
            onOpenChange={handleClose}
            title={title || "Reorder Modules"}
            description="Use the arrows to reorder modules. Changes will be saved when you click 'Save Order'."
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitText="Save Order"
            loading={loading}
            maxWidth="lg"
        >
            <div className="space-y-4">
                {moduleList.length > 0 ? (
                    <div className="space-y-2">
                        {moduleList.map((module, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-3 p-3 border rounded-lg bg-white hover:bg-brand-light-1 transition-colors"
                            >
                                <div className="flex flex-col gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => swapUp(index)}
                                        className="h-6 w-6 hover:bg-brand-accent/10"
                                    >
                                        <ChevronUp className="h-4 w-4 text-brand-dark" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => swapDown(index)}
                                        className="h-6 w-6 hover:bg-brand-accent/10"
                                    >
                                        <ChevronDown className="h-4 w-4 text-brand-dark" />
                                    </Button>
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-brand-dark">{module.moduleName}</p>
                                    <p className="text-sm text-brand-dark/70">Position: {index + 1}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-brand-dark/70 py-4">No modules to sort</p>
                )}

                {/* Error Message */}
                {formError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                )}

                {/* Success Message */}
                {formSuccess && (
                    <Alert variant="default" className="bg-green-50 border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                            {formSuccess}
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </FormDialog>
    );
}

export default SortModules;
